import React, { lazy, Suspense, useCallback, useMemo, useState } from 'react'
import { Divider, FormControlLabel, Grid, Radio, RadioGroup, Typography } from '@mui/material'
import { Controller, useFormContext } from 'react-hook-form'
import { Loader } from '@refera/ui-web'

import Accordion from '_/components/accordion'

import useStyles from './styles'
import {
  HELP_RESOURCES_SLUGS,
  NEGOTIATE_BUDGET_COLUMNS_OPTIONS,
  NEW_ORDERS_COLUMNS_OPTIONS,
  WAITING_BUDGET_COLUMNS_OPTIONS,
  WAITING_EXECUTION_COLUMNS_OPTIONS,
} from './constants'
import HelpInfoButton from '_/components/button/help-info-button'
import { getHelpResourcesSelector } from '_/modules/service-orders/selectors'
import { useSelector } from 'react-redux'

const HelpModal = lazy(() =>
  import('../help-modal').then(module => ({ default: module.HelpModal }))
)

const getHelpResources = (resources, slugs = []) => {
  return resources?.filter(resource => slugs.includes(resource.slug))
}

export function ColumnVisualization() {
  const styles = useStyles()
  const { control } = useFormContext()
  const helpResources = useSelector(getHelpResourcesSelector)
  const [helpModal, setHelpModal] = useState({
    open: false,
    modalData: {},
  })
  const helpModalResources = useMemo(() => {
    const resources = getHelpResources(helpResources, Object.values(HELP_RESOURCES_SLUGS))

    return resources?.reduce((acc, resource) => {
      acc[resource.slug] = resource
      return acc
    }, {})
  }, [helpResources])

  function Label({ text, optionNumber }) {
    return (
      <Typography>
        <strong className={styles.strong}>Opção {optionNumber}: </strong>
        {text}
      </Typography>
    )
  }

  const onHelpIconClick = useCallback(
    helpResourceSlug => {
      const resource = helpModalResources?.[helpResourceSlug]
      setHelpModal({
        open: true,
        modalData: {
          title: resource?.title,
          description: resource?.description,
          media: resource?.media,
        },
      })
    },
    [helpModalResources]
  )

  const onCloseHelpModal = useCallback(() => {
    setHelpModal({
      open: false,
      modalData: {},
    })
  }, [])

  return (
    <Accordion id="shown-columns" title="Colunas exibidas" className={styles.container}>
      <Grid className={styles.wrapper}>
        <Typography className={styles.title}>
          Visualização <b>Novos chamados</b>
          {helpModalResources?.[HELP_RESOURCES_SLUGS.newOrders] && (
            <HelpInfoButton
              title="Clique no ícone de ajuda para mais informações."
              style={{ marginTop: '-8px' }}
              onClick={() => onHelpIconClick(HELP_RESOURCES_SLUGS.newOrders)}
            />
          )}
        </Typography>
        <Divider className={styles.divider} />
        <Controller
          name="newOrders"
          control={control}
          render={({ value, onChange }) => {
            return (
              <RadioGroup
                classes={{ root: styles.radioGroupStyles }}
                onChange={e => onChange(e.target.value)}
                value={value}
              >
                {NEW_ORDERS_COLUMNS_OPTIONS.map(({ label, option }, index) => (
                  <FormControlLabel
                    key={option}
                    value={option}
                    control={<Radio checked={value === option} />}
                    label={<Label text={label} optionNumber={index + 1} />}
                    classes={{ root: styles.labelInputRadioButton }}
                  />
                ))}
              </RadioGroup>
            )
          }}
        />
      </Grid>
      <Grid className={styles.wrapper}>
        <Typography className={styles.title}>
          Visualização <b>Negociar orçamento</b>
          {helpModalResources?.[HELP_RESOURCES_SLUGS.negotiateBudget] && (
            <HelpInfoButton
              title="Clique no ícone de ajuda para mais informações."
              style={{ marginTop: '-8px' }}
              onClick={() => onHelpIconClick(HELP_RESOURCES_SLUGS.negotiateBudget)}
            />
          )}
        </Typography>
        <Divider className={styles.divider} />
        <Controller
          name="negotiateBudget"
          control={control}
          render={({ value, onChange }) => {
            return (
              <RadioGroup
                classes={{ root: styles.radioGroupStyles }}
                onChange={e => onChange(e.target.value)}
                value={value}
              >
                {NEGOTIATE_BUDGET_COLUMNS_OPTIONS.map(({ label, option }, index) => (
                  <FormControlLabel
                    key={option}
                    value={option}
                    control={<Radio checked={value === option} />}
                    label={<Label text={label} optionNumber={index + 1} />}
                    classes={{ root: styles.labelInputRadioButton }}
                  />
                ))}
              </RadioGroup>
            )
          }}
        />
      </Grid>
      <Grid className={styles.wrapper}>
        <Typography className={styles.title}>
          Visualização <b>Aguardando orçamento</b>
          {helpModalResources?.[HELP_RESOURCES_SLUGS.waitingBudget] && (
            <HelpInfoButton
              title="Clique no ícone de ajuda para mais informações."
              style={{ marginTop: '-8px' }}
              onClick={() => onHelpIconClick(HELP_RESOURCES_SLUGS.waitingBudget)}
            />
          )}
        </Typography>
        <Divider className={styles.divider} />
        <Controller
          name="waitingBudget"
          control={control}
          render={({ value, onChange }) => {
            return (
              <RadioGroup
                classes={{ root: styles.radioGroupStyles }}
                onChange={e => onChange(e.target.value)}
                value={value}
              >
                {WAITING_BUDGET_COLUMNS_OPTIONS.map(({ label, option }, index) => (
                  <FormControlLabel
                    key={option}
                    value={option}
                    control={<Radio checked={value === option} />}
                    label={<Label text={label} optionNumber={index + 1} />}
                    classes={{ root: styles.labelInputRadioButton }}
                  />
                ))}
              </RadioGroup>
            )
          }}
        />
      </Grid>
      <Grid className={styles.wrapper}>
        <Typography className={styles.title}>
          Visualização <b>Aguardando execução</b>
          {helpModalResources?.[HELP_RESOURCES_SLUGS.waitingExecution] && (
            <HelpInfoButton
              title="Clique no ícone de ajuda para mais informações."
              style={{ marginTop: '-8px' }}
              onClick={() => onHelpIconClick(HELP_RESOURCES_SLUGS.waitingExecution)}
            />
          )}
        </Typography>
        <Divider className={styles.divider} />
        <Controller
          name="waitingExecution"
          control={control}
          render={({ value, onChange }) => {
            return (
              <RadioGroup
                classes={{ root: styles.radioGroupStyles }}
                onChange={e => onChange(e.target.value)}
                value={value}
              >
                {WAITING_EXECUTION_COLUMNS_OPTIONS.map(({ label, option }, index) => (
                  <FormControlLabel
                    key={option}
                    value={option}
                    control={<Radio checked={value === option} />}
                    label={<Label text={label} optionNumber={index + 1} />}
                    classes={{ root: styles.labelInputRadioButton }}
                  />
                ))}
              </RadioGroup>
            )
          }}
        />
      </Grid>
      {helpModal?.open && (
        <Suspense fallback={<Loader hasBackdrop open />}>
          <HelpModal modalData={helpModal.modalData} closeModal={onCloseHelpModal} />
        </Suspense>
      )}
    </Accordion>
  )
}
